import { useStore } from "vuex";
import { getAdminInfo } from "@/modules/Admin/pages/AdminMain/api/index";

// Cache the in-flight promise so multiple callers share the same /me request.
// Must be reset on logout so the next user does not receive the previous user's branch_id.
let branchIdPromise = null;

/**
 * Reset the branch ID cache. Call this on logout to prevent memory leak
 * (next user would otherwise get previous user's branch_id from cached promise).
 * Can be imported and called from Vuex or anywhere (no composable context needed).
 */
export function resetBranchIdCache() {
  branchIdPromise = null;
}

/**
 * Composable for getting branch_id with a single /me call per session.
 * Reads from Vuex store first, then localStorage, then fetches from API.
 * Caches the fetch promise to prevent duplicate in-flight requests.
 */
export const useBranchId = () => {
  const store = useStore();

  const getBranchId = async () => {
    // Return cached promise if already fetching
    if (branchIdPromise) {
      return branchIdPromise;
    }

    // Try store first
    let branchId = store.state.branches?.active_branch;
    if (branchId) {
      return branchId;
    }

    // Try localStorage and sync to store
    const storedId = localStorage.getItem("branch_id");
    if (storedId) {
      branchId = parseInt(storedId, 10);
      // Branches module is not namespaced; mutation is global "setActiveBranchId"
      store.commit("setActiveBranchId", branchId);
      return branchId;
    }

    // Fetch from API (single cached promise)
    branchIdPromise = getAdminInfo()
      .then((res) => {
        const branches = res.data?.branches || [];
        if (branches.length > 0) {
          const id = branches[0]?.id;
          store.commit("setActiveBranchId", id);
          localStorage.setItem("branch_id", String(id));
          localStorage.setItem(
            "admin_branches",
            JSON.stringify(branches)
          );
          return id;
        }
        throw new Error("No branches found");
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch admin info (useBranchId):", err);
        throw err;
      })
      .finally(() => {
        branchIdPromise = null;
      });

    return branchIdPromise;
  };

  return { getBranchId, resetBranchIdCache };
};
